import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { EntityPin } from "@mapmycustomers/shared/types/map";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import EntityFeature from "@app/scene/map/components/features/EntityFeature";
import StickyBar from "@app/scene/map/components/tools/StickyBar";
import {
  hideSidebar,
  setFocusedEntity,
  setPinHover,
  showAnnotation,
} from "@app/scene/map/store/mapMode/actions";
import { getFocusedEntity, isSidebarVisible } from "@app/scene/map/store/mapMode/selectors";
import { hideEntityView, showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { FOCUSED_ENTITY_ZINDEX } from "@app/util/map/consts";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import isValidMapEntry from "@app/util/map/isValidMapEntry";

import styles from "./FocusedEntity.module.scss";

interface Props {
  focusedEntity?: EntityPin;
  hideEntityView: typeof hideEntityView;
  hideSidebar: typeof hideSidebar;
  isSidebarVisible: boolean;
  onShowAnnotation: typeof showAnnotation.request;
  setFocusedEntity: typeof setFocusedEntity;
  setPinHover: typeof setPinHover;
  showEntityView: typeof showEntityView;
}

const FocusedEntity: React.FC<Props> = ({
  focusedEntity,
  hideEntityView,
  hideSidebar,
  isSidebarVisible,
  onShowAnnotation,
  setFocusedEntity,
  setPinHover,
  showEntityView,
}) => {
  const { map } = useMap();
  const intl = useIntl();
  const [previousZoom, setPreviousZoom] = useState<number>(1);
  const [previousCenter, setPreviousCenter] = useState<google.maps.LatLng | undefined>();
  const [previousSideBarVisibility, setPreviousSideBarVisibility] = useState<boolean>(false);
  const [savedFocusedEntity, setSavedFocusedEntity] = useState<string | undefined>();

  const handlePinClick = useCallback(
    (pin) => {
      onShowAnnotation(mapPinIdGetter(pin));
    },
    [onShowAnnotation]
  );

  const revertMapState = useCallback(() => {
    map.setZoom(previousZoom);
    if (previousCenter) {
      map.setCenter(previousCenter);
    }
    if (!previousSideBarVisibility) {
      hideSidebar();
    }
    setSavedFocusedEntity(undefined);
  }, [
    hideSidebar,
    map,
    previousCenter,
    previousZoom,
    previousSideBarVisibility,
    setSavedFocusedEntity,
  ]);

  const handlePinDoubleClick = useCallback(
    (pin) => {
      setFocusedEntity(pin);
      setPinHover(mapPinIdGetter(pin));
      showEntityView({
        entityId: pin.data.id,
        entityType: pin.data.entity,
      });
    },
    [showEntityView, setFocusedEntity, setPinHover]
  );

  useEffect(() => {
    if (isValidMapEntry(focusedEntity)) {
      const zoom = map.getZoom() ?? 12;
      const entityHash = `${focusedEntity.data.entity}_${focusedEntity.data.id}`;
      if (entityHash !== savedFocusedEntity) {
        if (!savedFocusedEntity) {
          setPreviousZoom(zoom);
          setPreviousCenter(map.getCenter());
          setPreviousSideBarVisibility(isSidebarVisible);
        }
        setSavedFocusedEntity(entityHash);
      }
      map.setCenter({
        lat: focusedEntity.region!.latitude,
        lng: focusedEntity.region!.longitude,
      });
      map.setZoom(zoom < 12 ? 12 : zoom);
      setPinHover(mapPinIdGetter(focusedEntity));
    }
  }, [
    focusedEntity,
    isSidebarVisible,
    map,
    previousCenter,
    savedFocusedEntity,
    setPinHover,
    setPreviousCenter,
    setPreviousZoom,
    setSavedFocusedEntity,
  ]);

  const handleCancel = useCallback(
    (dropMapView?: boolean) => {
      setFocusedEntity(undefined);
      if (dropMapView) {
        revertMapState();
        hideEntityView();
      }
    },
    [hideEntityView, revertMapState, setFocusedEntity]
  );

  if (!focusedEntity) {
    return null;
  }

  return (
    <>
      <Interactive>
        <StickyBar className={styles.container}>
          <div className={styles.description}>
            {intl.formatMessage({
              id: "map.focusedEntity.text",
              defaultMessage: "Your map view was changed to focus on the record you selected",
              description: "Map focused pin description",
            })}
          </div>

          <Row gutter={layout.spacerM}>
            <Col>
              <Button onClick={() => handleCancel(true)} type="text">
                {intl.formatMessage({
                  id: "map.focusedEntity.back",
                  defaultMessage: "Back to Previous View",
                  description: "Map focused back action",
                })}
              </Button>
            </Col>
            <Col>
              <Button onClick={() => handleCancel()}>
                {intl.formatMessage({
                  id: "map.focusedEntity.stay",
                  defaultMessage: "Stay Here",
                  description: "Map focused stay action",
                })}
              </Button>
            </Col>
          </Row>
        </StickyBar>
      </Interactive>
      <EntityFeature
        entityPins={[focusedEntity]}
        onClick={handlePinClick}
        onDoubleClick={handlePinDoubleClick}
        type="focused_entity"
        zIndex={FOCUSED_ENTITY_ZINDEX}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  focusedEntity: getFocusedEntity(state),
  isSidebarVisible: isSidebarVisible(state),
});

const mapDispatchToProps = {
  hideEntityView,
  hideSidebar,
  onShowAnnotation: showAnnotation.request,
  setFocusedEntity,
  setPinHover,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(FocusedEntity);
